/**
 * Report-type registry for the "visual report generation" feature.
 *
 * A meeting can become a self-contained, Lexicanon-themed visual HTML *report*
 * whose TYPE is data-driven: each report type = metadata + two prompts (a
 * **fit prompt** used to detect whether a meeting suits the type, and a
 * **generation prompt** used to render the report). Platform-default types are
 * editable DB rows (super-admin can add types as data — no deploy); a company
 * can override them per-org.
 *
 * This file is the shared contract every layer depends on. It mirrors the
 * "typed registry + pure resolve function" shape of `summary-blocks.ts`:
 * `REPORT_TYPES_SEED` is the canonical platform seed, and
 * `resolveReportTypes(platformRows, orgRows)` is the pure merge the backend
 * runs to produce the effective set for an org (same "registry is source of
 * truth, org overrides" spirit as `resolveSummaryLayout`).
 *
 * See tasks/ + the report-type taxonomy spec (`tmp/report-type-taxonomy.html`).
 */

/**
 * Optional per-type generation hints — steer the generator toward the diagrams,
 * KPIs, and section order that fit the type. Advisory, not enforced.
 */
export interface IReportTypeHints {
	/** Diagram kinds this type favors (e.g. "flowchart TD", "roadmap timeline"). */
	favoredDiagrams?: string[];
	/** What numbers rise to the top for this audience. */
	kpiFocus?: string[];
	/** The default section order (the type's "spine"). */
	sectionSpine?: string[];
}

/**
 * One report type. Platform defaults are seeded with `isBuiltin: true`; a
 * per-org row can override `enabled`, both prompts, `threshold`, `label`, and
 * `hints` (see {@link resolveReportTypes}). Org-authored types are appended as
 * custom types (`isBuiltin: false`).
 */
export interface IReportTypeDef {
	/** Stable slug — the discriminant everything keys on (e.g. "product_spec"). */
	id: string;
	/** Human label for the picker + admin UI. */
	label: string;
	/** Audience descriptor (e.g. "CFO · finance"). */
	audience: string;
	/** Detection prompt — describes when a meeting fits this type. */
	fitPrompt: string;
	/** Generation prompt — instructs the generator how to render this type. */
	generationPrompt: string;
	/** Optional generation hints (diagrams / KPIs / section spine). */
	hints?: IReportTypeHints;
	/** Whether this type is offered/considered at all. */
	enabled: boolean;
	/** Confidence-to-suggest gate, 0–100 percent. */
	threshold: number;
	/** Template this type belongs to (e.g. "meeting-intelligence"). */
	templateScope: string;
	/** True for platform-seeded types; false for org-authored custom types. */
	isBuiltin: boolean;
}

/**
 * The result of cheap/free detection for one meeting: does any report type fit,
 * which one is best, and how confident. Detection only *suggests* — generation
 * is a separate, on-demand step. `confidence` is 0–1 (the `threshold` above is
 * 0–100, so compare against `threshold / 100`).
 */
export interface IReportFit {
	/** Whether the meeting is report-worthy at all (past the heuristic gate). */
	eligible: boolean;
	/** Best-fit type id, or null when nothing fits. */
	suggestedType: string | null;
	/** Confidence in `suggestedType`, 0–1. */
	confidence: number;
	/** Free-form detection signals (cue hits, model rationale, etc.). */
	signals?: Record<string, unknown>;
	/** Runner-up type id for the "same meeting, second audience" case. */
	secondaryType?: string | null;
	/** Dominant transcript language (BCP-47-ish, e.g. "en", "nl"). */
	language?: string;
}

/** One KPI tile in a report plan. */
export interface IReportPlanKpi {
	value: string | number;
	label: string;
	note?: string;
}

/** One section of a report plan. `content` shape varies by `kind`. */
export interface IReportPlanSection {
	id: string;
	title: string;
	kind: string;
	content?: unknown;
}

/** One diagram in a report plan. `spec` varies by `kind` (Mermaid source, chart config, …). */
export interface IReportPlanDiagram {
	id: string;
	kind: string;
	title: string;
	spec: unknown;
}

/**
 * The structured outline the generator produces from a transcript and then
 * renders into themed HTML. Loosely typed where content varies by report type —
 * the generator fills what the type + transcript support and the renderer maps
 * it into the deterministic scaffold.
 */
export interface IReportPlan {
	title: string;
	subtitle?: string;
	/** Dominant transcript language the report is written in. */
	language: string;
	/** The resolved report type id this plan was built for. */
	reportType: string;
	kpis?: IReportPlanKpi[];
	sections: IReportPlanSection[];
	diagrams?: IReportPlanDiagram[];
	/** Decisions captured (shape varies — e.g. decision_log rows). */
	decisions?: unknown;
	/** Questions the meeting left open. */
	openQuestions?: unknown;
	/** Owner-tagged next steps. */
	actionItems?: unknown;
	/** Visible caveats for inferred/implied facts (faithfulness footer). */
	provenanceNotes?: string[];
}

/**
 * Metadata stored alongside a generated report artifact — shared by the generator
 * (WS1), the serving endpoints (WS3), and the viewer/banner (WS4). Persisted with
 * the `session_artifacts` row so the UI can label/list a report and the engine can
 * later decide whether to re-render (e.g. when a type's prompt changes).
 */
export interface IReportArtifactMeta {
	/** The resolved report type id this artifact was generated for. */
	reportType: string;
	/** Language the report was written in (dominant transcript language). */
	language: string;
	title: string;
	/** Prompt/version stamp — lets a future "re-render on prompt change" compare. */
	promptVersion: string;
	diagramCount: number;
	/** Section titles in render order. */
	sections: string[];
	/** Validate-gate quality score, 0–1. */
	qualityScore?: number;
	/** Visible caveats surfaced from the plan's provenance footer. */
	provenanceNotes?: string[];
}

/**
 * Canonical platform seed — the 6 designed types + a generic fallback, scoped to
 * the meeting-intelligence template. Prompts, hints, and thresholds come from the
 * report-type taxonomy spec. Super-admin edits these as DB rows after seeding;
 * this array is only the first-run bootstrap (seeded ON CONFLICT DO NOTHING, so
 * it never clobbers operator edits).
 */
export const REPORT_TYPES_SEED: readonly IReportTypeDef[] = [
	{
		id: "product_spec",
		label: "Product spec",
		audience: "Product · eng",
		fitPrompt:
			"Fits when something is being built — scope is debated, requirements and named systems/integrations come up, a discovery or scoping call that could become a spec a PM could ship. Cues (EN): in / out of scope, MVP, must / should, requirement, API / endpoint, integration, screen / flow, phase 1. (NL): in / buiten scope, MVP, moet kunnen, koppeling, scherm, fase 1, eerst… dan. NOT a pure decisions-and-rationale recap (decision_log) or a finance discussion (cfo_brief).",
		generationPrompt:
			"You render a product specification from a meeting transcript in {language}. Centerpiece: a scope table (In / Out / Later / Undecided). Extract every named system/integration, screen, requirement, hard number and deadline. Draw architecture and lifecycle as Mermaid (≤12 nodes, quoted labels). Ground every requirement in the transcript; label inferred requirements as inference in a provenance note; never invent scope. Preserve uncertainty — what the meeting left undecided stays undecided.",
		hints: {
			favoredDiagrams: ["architecture flowchart", "lifecycle flowchart (TD)", "hand-off sequence"],
			kpiFocus: ["deadlines", "user / volume counts", "key money figures"],
			sectionSpine: [
				"Overview + KPIs",
				"Context (why)",
				"Assignment (what)",
				"Architecture",
				"Key flows",
				"Integrations",
				"Scope table",
				"Open questions",
				"Actions",
			],
		},
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "decision_log",
		label: "Decision log",
		audience: "Leadership",
		fitPrompt:
			"Fits when the meeting decided several things and weighed alternatives — options compared, trade-offs made — but isn't building one artifact. Cues (EN): we decided, let's go with, agreed, alternative, trade-off, instead of, rejected, for now. (NL): we gaan voor, besloten, beslissing, alternatief, in plaats van, voor nu, afgewezen. NOT a single build spec (product_spec) or a workflow definition (process_design).",
		generationPrompt:
			"You render a decision log in {language}. Centerpiece: a decision table (decision · rationale · alternatives considered · status: agreed | undecided | rejected · owner). Capture the reasoning, not just the outcome. Mark undecided items clearly and never resolve what the meeting left open. Ground every decision in the transcript; put any inferred owner or rationale in a provenance note.",
		hints: {
			favoredDiagrams: ["decision flow (light)"],
			kpiFocus: ["# decisions", "# open", "# rejected"],
			sectionSpine: ["Overview", "Decisions", "Context per decision", "Open / undecided", "Actions"],
		},
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "cfo_brief",
		label: "Financial brief",
		audience: "CFO · finance",
		fitPrompt:
			"Fits when budget, cost, revenue, pricing, runway or forecasts dominate the meeting — a numbers discussion for a CFO / finance audience. Cues (EN): budget, cost / spend, revenue / margin, runway, forecast, cashflow, pricing, ARR / MRR, invoice. (NL): budget, kosten, omzet / marge, begroting, prognose, cashflow, prijs, facturatie. This is a numbers type, not a flow type — NOT a sales-pipeline discussion (cco_brief) or a build spec (product_spec).",
		generationPrompt:
			"You render a financial brief for a CFO in {language}. Centerpiece: a figures table (line items with cost / revenue / variance) and a KPI band of every hard figure. Numbers verbatim — never round, never convert currency. Every projection or estimate is labeled as a projection, not a fact, with the assumption stated in an Assumptions section. Add a Financial risks section. Prefer tables and a single bar/trend chart over flow diagrams. Match the transcript's language.",
		hints: {
			favoredDiagrams: ["bar chart", "trend chart"],
			kpiFocus: ["currency figures", "% / margins", "target vs actual", "deadlines"],
			sectionSpine: [
				"Executive summary",
				"KPI band + figures table",
				"Assumptions",
				"Financial risks",
				"Asks / decisions",
				"Actions",
			],
		},
		enabled: true,
		threshold: 65,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "cco_brief",
		label: "Commercial brief",
		audience: "CCO · sales",
		fitPrompt:
			"Fits when the meeting is about pipeline, deals, accounts, churn, quota or go-to-market — outcome-oriented, for a CCO / sales audience. Cues (EN): pipeline, deal, lead / prospect, quota / target, churn, upsell, account, win / loss, competitor. (NL): pipeline, deal, lead / klant, target, opzeggen / churn, upsell, account, concurrent, partner. NOT a pure finance/budget discussion (cfo_brief).",
		generationPrompt:
			"You render a commercial brief for a CCO in {language}. Centerpiece: a pipeline/deal table (account · stage · value · owner · next step) and a targets RAG band. Foreground deal values, targets, conversion and churn. Owner-tag every next step. Ground figures and account names in the transcript; label any inference. Match the transcript's language.",
		hints: {
			favoredDiagrams: ["funnel", "stage bar (CSS)"],
			kpiFocus: ["deal values", "quota / target", "conversion", "churn %", "close dates"],
			sectionSpine: [
				"Summary",
				"Targets (RAG)",
				"Pipeline / deals",
				"Accounts / competitive",
				"Risks",
				"Actions (owner-tagged)",
			],
		},
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "process_design",
		label: "Process / SOP",
		audience: "Ops",
		fitPrompt:
			"Fits when a workflow or procedure is defined — steps, hand-offs, roles, escalation — an SOP for an ops audience. Cues (EN): process / SOP, step, first… then, hand-off, escalation, trigger, who does what, cronjob / webhook. (NL): proces, stap, eerst… dan, overdracht, escalatie, wie doet wat, trigger, rol. NOT a one-off set of decisions (decision_log) or a product build spec (product_spec).",
		generationPrompt:
			"You render a process/SOP from a meeting in {language}. Centerpiece: a numbered step-by-step with an edge-case/exception table and the roles involved. Draw the flow as a Mermaid flowchart (TD) and hand-offs as a swimlane sequence. Be exhaustive on edge cases — they are the value of an SOP. Capture triggers, SLAs and owners. Ground every step in the transcript; label inference.",
		hints: {
			favoredDiagrams: ["flowchart TD", "swimlane sequence"],
			kpiFocus: ["SLA times", "frequencies", "volumes"],
			sectionSpine: [
				"Purpose",
				"Actors / roles",
				"Step-by-step flow",
				"Edge cases & exceptions",
				"Triggers / SLA",
				"Owners",
			],
		},
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "strategy_brief",
		label: "Strategic brief",
		audience: "Strategy · exec/founders",
		fitPrompt:
			"Fits when the meeting is about setting direction, vision, priorities, or long-term strategy — someone synthesizing where the org or product is going. Cues (EN): strategy, vision, direction, priorities, roadmap, long-term, bets, north star, market, positioning, OKR, opportunity, threat, next year/quarter. (NL): strategie, visie, richting, prioriteiten, lange termijn, kansen, bedreigingen, waar gaan we heen. NOT a single-feature build (product_spec) or pure finance (cfo_brief).",
		generationPrompt:
			"You render a strategic brief for an exec / strategic director in {language}. Centerpiece: strategic themes → a priorities/bets table → opportunities & risks → a directional horizon/roadmap. Synthesize the direction discussed. Ground every theme, bet, and number in the transcript; label inference in a provenance note; preserve uncertainty. Big-picture, forward-looking tone. Prefer a roadmap/horizon timeline over flow diagrams.",
		hints: {
			favoredDiagrams: ["roadmap timeline", "themes"],
			kpiFocus: ["north-star metrics", "targets", "time horizons"],
			sectionSpine: ["Themes", "Priorities / bets", "Opportunities & risks", "Horizon / roadmap", "Actions"],
		},
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
	{
		id: "generic_visual",
		label: "Visual brief",
		audience: "Fallback",
		fitPrompt:
			"The safety net. Fits when the meeting is report-worthy — substantive content worth a visual deliverable — but none of the specialized types (product_spec, decision_log, cfo_brief, cco_brief, process_design, strategy_brief) fit cleanly. Prefer a specialized type whenever one clearly matches; fall back to this only when confidence in all of them is low. If the meeting isn't report-worthy at all, suggest nothing.",
		generationPrompt:
			"You render a clean visual brief from a meeting transcript in {language} when no specialized report type fits cleanly. Centerpiece: key-point cards; choose the sections and diagrams the content actually supports rather than forcing a fixed structure. Use a Mermaid diagram only when the content is genuinely a flow or structure; otherwise use tables and cards. Ground every point, figure, date and owner in the transcript; label any inference in a provenance note; preserve uncertainty and match the transcript's language.",
		enabled: true,
		threshold: 60,
		templateScope: "meeting-intelligence",
		isBuiltin: true,
	},
];

/** A row is usable only if it carries a non-empty string id. */
function isValidReportTypeDef(row: IReportTypeDef | null | undefined): row is IReportTypeDef {
	return !!row && typeof row.id === "string" && row.id.length > 0;
}

/**
 * Inherit the base value when the override string is empty/whitespace. A
 * toggle-only or partial org row can carry blank prompt/label columns; those
 * must NOT blank out the builtin (an empty string is "defined", so `??` would
 * wrongly keep it).
 */
function inheritStr(override: string | undefined, base: string): string {
	return typeof override === "string" && override.trim().length > 0 ? override : base;
}

/**
 * Apply a per-org override onto a platform base. Only the operator-tunable fields
 * ride the override — `enabled`, both prompts, `threshold`, `label`, `hints`.
 * String fields inherit the base when the override is blank (see
 * {@link inheritStr}), so a toggle-only override can't wipe a builtin's prompts.
 * Identity fields (`id`, `audience`, `templateScope`, `isBuiltin`) stay from the
 * platform base so an org can't relabel a builtin's audience or forge a builtin.
 */
function applyOrgOverride(base: IReportTypeDef, override: IReportTypeDef): IReportTypeDef {
	return {
		...base,
		label: inheritStr(override.label, base.label),
		enabled: override.enabled,
		fitPrompt: inheritStr(override.fitPrompt, base.fitPrompt),
		generationPrompt: inheritStr(override.generationPrompt, base.generationPrompt),
		threshold: override.threshold ?? base.threshold,
		hints: override.hints ?? base.hints,
	};
}

/**
 * Resolve the effective report types for an org from platform defaults + per-org
 * rows. Pure — no I/O; the backend reader hands it already-fetched rows.
 *
 * Merge rules (same spirit as `resolveSummaryLayout`):
 *  - Platform rows (`isBuiltin`) are the source of truth: each appears in the
 *    result, so a type ADDED later shows up for every org automatically.
 *  - An org row with a matching `id` overrides that type's `enabled`, prompts,
 *    `threshold`, `label`, and `hints` (see {@link applyOrgOverride}).
 *  - Org rows whose `id` is not a platform type are appended as custom types
 *    (`isBuiltin: false`).
 *  - Malformed rows (no id) on either side are ignored (forward-compat).
 *
 * No org rows (a company that never customised) → the platform seed as-is.
 */
export function resolveReportTypes(
	platformRows: readonly IReportTypeDef[],
	orgRows: readonly IReportTypeDef[],
): IReportTypeDef[] {
	const orgById = new Map<string, IReportTypeDef>();
	for (const row of orgRows) {
		if (isValidReportTypeDef(row)) orgById.set(row.id, row);
	}

	const seen = new Set<string>();
	const resolved: IReportTypeDef[] = [];

	// Platform rows first, each overlaid by any matching org override.
	for (const base of platformRows) {
		if (!isValidReportTypeDef(base) || seen.has(base.id)) continue;
		seen.add(base.id);
		const override = orgById.get(base.id);
		resolved.push(override ? applyOrgOverride(base, override) : base);
	}

	// Org-only ids → appended as custom types.
	for (const row of orgRows) {
		if (!isValidReportTypeDef(row) || seen.has(row.id)) continue;
		seen.add(row.id);
		resolved.push({ ...row, isBuiltin: false });
	}

	return resolved;
}
